import { useState } from 'react';
import { Edit2, Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useAddInvoiceLineItem } from './useAddInvoiceLineItem';
import { useUpdateInvoiceLineItem } from './useUpdateInvoiceLineItem';
import { useDeleteInvoiceLineItem } from './useDeleteInvoiceLineItem';

// The line-item editor InvoicesSection has always rendered inside an expanded
// invoice, lifted out unchanged so the invoice row expander can use the same
// one (ruling 27) rather than growing a second editor that drifts from this.
//
// A `discount` line is stored positive and SUBTRACTS from the invoice total, so
// it is shown with a minus sign; nothing else about it is special.

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

function formatCurrency(cents: number) {
  return currencyFormatter.format((cents || 0) / 100);
}

export interface EditableLineItem {
  id: string;
  description: string;
  amountCents: number;
  lineType: string;
}

export function effectiveAmountCents(item: EditableLineItem) {
  return item.lineType === 'discount' ? -item.amountCents : item.amountCents;
}

interface LineItemRowProps {
  item: EditableLineItem;
  isDraft: boolean;
  onUpdate: (itemId: string, updates: { description: string; amountCents: number }) => void;
  onDelete: (itemId: string) => void;
  updating: boolean;
  deleting: boolean;
}

export function LineItemRow({ item, isDraft, onUpdate, onDelete, updating, deleting }: LineItemRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editAmount, setEditAmount] = useState((item.amountCents / 100).toFixed(2));
  const [editDescription, setEditDescription] = useState(item.description);

  const handleSave = () => {
    onUpdate(item.id, {
      description: editDescription,
      amountCents: Math.round(parseFloat(editAmount) * 100)
    });
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <tr className="border-b">
        <td className="py-2 text-sm">{item.description}</td>
        <td className="py-2 text-sm text-right">
          {item.lineType === 'discount' ? `-${formatCurrency(item.amountCents)}` : formatCurrency(item.amountCents)}
        </td>
        <td className="py-2 text-sm">
          <Badge variant="outline" className="text-xs">{item.lineType}</Badge>
        </td>
        {isDraft && (
          <td className="py-2 text-right">
            <div className="flex gap-1 justify-end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${item.description}`}
                onClick={() => setIsEditing(true)}
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${item.description}`}
                onClick={() => onDelete(item.id)}
                disabled={deleting}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr className="border-b bg-muted/50">
      <td className="py-2">
        <Input
          aria-label="Line item description"
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          className="text-sm"
        />
      </td>
      <td className="py-2">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">$</span>
          <Input
            aria-label="Line item amount"
            type="number"
            step="0.01"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            className="text-sm w-24 text-right"
          />
        </div>
      </td>
      <td className="py-2">
        <Badge variant="outline" className="text-xs">{item.lineType}</Badge>
      </td>
      <td className="py-2 text-right">
        <div className="flex gap-1 justify-end">
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updating}>
            {updating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

interface InvoiceLineItemEditorProps {
  invoiceId: string;
  lineItems: readonly EditableLineItem[];
  editable: boolean;
  totalCents: number;
  onChanged: () => void;
}

// The whole table plus its add/edit/delete wiring, so a caller only has to say
// which invoice it is and whether the rows may be touched.
export function InvoiceLineItemEditor({
  invoiceId,
  lineItems,
  editable,
  totalCents,
  onChanged,
}: InvoiceLineItemEditorProps) {
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', amount: '0.00', lineType: 'other' });
  const [actionError, setActionError] = useState<string | null>(null);

  const [addLineItem, { loading: addingItem }] = useAddInvoiceLineItem();
  const [updateLineItem, { loading: updatingItem }] = useUpdateInvoiceLineItem();
  const [deleteLineItem, { loading: deletingItem }] = useDeleteInvoiceLineItem();

  const report = (message: string | null | undefined) => {
    setActionError(message || null);
    if (!message) onChanged();
  };

  const handleAddItem = async () => {
    const amountCents = Math.round(parseFloat(newItem.amount || '0') * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setActionError('Amount must be greater than $0.');
      return;
    }

    const result = await addLineItem({
      variables: {
        input: {
          invoiceId,
          description: newItem.description,
          amountCents,
          lineType: newItem.lineType,
        },
      },
    });

    const message = result.data?.addInvoiceLineItem?.userErrors?.[0]?.message;
    if (message) {
      setActionError(message);
      return;
    }

    setShowAddItemDialog(false);
    setNewItem({ description: '', amount: '0.00', lineType: 'other' });
    report(null);
  };

  const handleUpdateItem = async (
    itemId: string,
    updates: { description: string; amountCents: number }
  ) => {
    const result = await updateLineItem({ variables: { input: { lineItemId: itemId, ...updates } } });
    report(result.data?.updateInvoiceLineItem?.userErrors?.[0]?.message);
  };

  const handleDeleteItem = async (itemId: string) => {
    const result = await deleteLineItem({ variables: { input: { lineItemId: itemId } } });
    report(result.data?.deleteInvoiceLineItem?.userErrors?.[0]?.message);
  };

  return (
    <div className="space-y-2">
      {actionError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      <table className="w-full">
        <thead>
          <tr className="border-b text-muted-foreground text-sm">
            <th className="text-left py-2">Description</th>
            <th className="text-right py-2 w-32">Amount</th>
            <th className="text-left py-2 w-24">Type</th>
            {editable && <th className="w-24"></th>}
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item) => (
            <LineItemRow
              key={item.id}
              item={item}
              isDraft={editable}
              onUpdate={handleUpdateItem}
              onDelete={handleDeleteItem}
              updating={updatingItem}
              deleting={deletingItem}
            />
          ))}
          {lineItems.length === 0 && (
            <tr>
              <td colSpan={editable ? 4 : 3} className="py-4 text-center text-muted-foreground text-sm">
                No line items
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="font-medium">
            <td className="py-2">Total</td>
            <td className="py-2 text-right">{formatCurrency(totalCents)}</td>
            <td colSpan={editable ? 2 : 1}></td>
          </tr>
        </tfoot>
      </table>

      {editable && (
        <Button variant="outline" size="sm" onClick={() => setShowAddItemDialog(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Line Item
        </Button>
      )}

      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription>
              Add a charge or a discount to this draft invoice. Its total is recalculated from the lines.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`add-line-description-${invoiceId}`}>Description</Label>
              <Input
                id={`add-line-description-${invoiceId}`}
                value={newItem.description}
                onChange={(event) => setNewItem({ ...newItem, description: event.target.value })}
                placeholder="Lab fee"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={`add-line-amount-${invoiceId}`}>Amount</Label>
                <Input
                  id={`add-line-amount-${invoiceId}`}
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={newItem.amount}
                  onChange={(event) => setNewItem({ ...newItem, amount: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newItem.lineType}
                  onValueChange={(value) => setNewItem({ ...newItem, lineType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tuition">Tuition</SelectItem>
                    <SelectItem value="enrollment_fee">Enrollment fee</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="discount">Discount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
            <Button onClick={() => { void handleAddItem(); }} disabled={addingItem}>
              {addingItem ? 'Adding...' : 'Add Line Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default InvoiceLineItemEditor;
